// src/utils/datesBR.js
// Helpers nativos (sem libs) com fuso dinâmico, sincronizado via header `x-timezone`

let CURRENT_TZ =
  (typeof document !== "undefined" &&
    document.querySelector('meta[name="app-timezone"]')?.getAttribute("content")) ||
  "America/Sao_Paulo";

export function setTZ(tz) {
  if (tz && typeof tz === "string") CURRENT_TZ = tz;
}
export function getTZ() {
  return CURRENT_TZ;
}

const RE_MYSQL_DATE     = /^\d{4}-\d{2}-\d{2}$/;                        // 2025-10-28
const RE_MYSQL_DATETIME = /^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}:\d{2}$/;  // 2025-10-28 12:34:56
const RE_BR_SLASH       = /^(\d{2})\/(\d{2})\/(\d{4})(?: .*)?$/;         // 28/10/2025...

/** Formata um Date no fuso atual como YYYY-MM-DD (aceito por <input type="date">) */
function fmtKey(d) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CURRENT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const obj = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${obj.year}-${obj.month}-${obj.day}`;
}

/** Converte "qualquer coisa" em YYYY-MM-DD com segurança */
export function toKeyYYYYMMDD(input) {
  if (input == null || input === "") return "";

  if (typeof input === "string") {
    const s = input.trim();

    // Já no formato
    if (RE_MYSQL_DATE.test(s)) return s;

    // Datetime "YYYY-MM-DD HH:mm:ss" ou "YYYY-MM-DDTHH:mm:ss" -> pega a data
    const m1 = s.match(RE_MYSQL_DATETIME);
    if (m1) return m1[1];

    // dd/mm/yyyy
    const m2 = s.match(RE_BR_SLASH);
    if (m2) return `${m2[3]}-${m2[2]}-${m2[1]}`;

    // ISO com offset/Z
    if (s.includes("T")) {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? "" : fmtKey(d);
    }

    // Fallback: "YYYY-MM-DD HH:mm:ss" sem TZ -> interpreta como UTC
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
      const d = new Date(s.replace(" ", "T") + "Z");
      return Number.isNaN(d.getTime()) ? "" : fmtKey(d);
    }
  }

  // Date / epoch
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? "" : fmtKey(d);
}

/** Mostra HH:mm a partir de "HH:mm[:ss]" | ISO | minutos inteiros | Date */
export function toHHMM(value) {
  if (value == null || value === "") return "—";
  const s = String(value).trim();

  // Já está pronto
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);

  // Minutos inteiros
  if (/^\d+$/.test(s)) {
    const total = parseInt(s, 10);
    const h = String(Math.floor(total / 60)).padStart(2, "0");
    const m = String(total % 60).padStart(2, "0");
    return `${h}:${m}`;
  }

  // ISO/Date
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: CURRENT_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const obj = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return `${obj.hour}:${obj.minute}`;
  }

  return s;
}

/** Hoje em YYYY-MM-DD (no fuso atual) */
export const todayKeyBR = () => toKeyYYYYMMDD(new Date());

/** Primeiro dia do mês atual em YYYY-MM-DD (no fuso atual) */
export function monthStartKeyBR() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CURRENT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  // Usa '01' e formata no fuso para garantir consistência
  return `${y}-${m}-01`;
}

/** (Opcional) Adiciona __dataKey (YYYY-MM-DD) recursivamente sem mutar datas originais */
export function attachDateKeys(payload) {
  if (payload == null) return payload;
  if (Array.isArray(payload)) return payload.map(attachDateKeys);
  if (typeof payload !== "object") return payload;

  const out = { ...payload };
  if ("data" in out) out.__dataKey = toKeyYYYYMMDD(out.data);
  for (const k of Object.keys(out)) {
    out[k] = attachDateKeys(out[k]);
  }
  return out;
}

/**
 * Converte "YYYY-MM-DD HH:mm[:ss]" (sem offset) para epoch considerando sempre CURRENT_TZ.
 * Se já vier com offset/Z, devolve o epoch normal do Date.
 * Retorna NaN em caso de falha.
 */
export function toEpochInTZ(value, tz = CURRENT_TZ) {
  if (!value) return NaN;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.getTime();

  const s = String(value).trim();
  if (!s) return NaN;

  // Se já tem offset/Z, deixa o Date resolver
  if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = new Date(s.replace(" ", "T")).getTime();
    return Number.isNaN(t) ? NaN : t;
  }

  // Aceita "YYYY-MM-DD HH:mm[:ss]" ou com "T"
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return NaN;

  const [_, y, mo, d, hh, mm, ss = "00"] = m;
  const baseUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), Number(ss));

  // Offset fixo para America/Sao_Paulo (UTC-3, sem DST atualmente)
  const offsetMsByTz =
    tz && tz.toLowerCase().includes("sao_paulo")
      ? -3 * 3600 * 1000
      : tz && tz.toLowerCase().includes("brasilia")
      ? -3 * 3600 * 1000
      : 0;

  // Converte horário local -> UTC epoch: utc = local - offset  => baseUtc - (-3h) = baseUtc + 3h
  const epoch = baseUtc - offsetMsByTz;
  return Number.isNaN(epoch) ? NaN : epoch;
}
